import IntegrationLoadingModalComponent from "@/components/LocationApiManagement/StateTraceability/IntegrationLoadingModal/IntegrationLoadingModal.component";
import IntegrationPopUpModalComponent from "@/components/LocationApiManagement/StateTraceability/IntegrationPopUpModal/IntegrationPopUpModal.component";
import AlertComponent from "@/components/sharedComponents/alert/alert.component";
import ConfirmModalComponent from "@/components/sharedComponents/confirm/confirm.component";
import { EventBus } from "@/event-bus";
import { Location } from "@/interfaces/location";
import {
  ModalIntegrationPopUp,
  ModalNotification
} from "@/interfaces/notification";
import {
  BiotrackIntegrationModel,
  ConfigValue,
  IntegrationModel,
  TraceabilityConfig
} from "@/interfaces/traceability";
import { i18n } from "@/plugins/i18n";
import { locationsService } from "@/services/locations.service";
import { messagesService } from "@/services/messages.service";
import { traceabilityService } from "@/services/traceability.service";
import {
  HelixDatePickerComponent,
  PageTitleComponent
} from "helix-vue-components";
import { Validator } from "vee-validate";
import { Component, Inject, Vue } from "vue-property-decorator";
import IntegrationLoadingModal from "../IntegrationLoadingModal/IntegrationLoadingModal.component";
import Template from "./IntegrationEdit.template.vue";

@Component({
  mixins: [Template],
  components: {
    PageTitleComponent,
    AlertComponent,
    IntegrationPopUpModalComponent,
    ConfirmModalComponent,
    HelixDatePickerComponent
  }
})
export default class IntegrationEditComponent extends Vue {
  @Inject() public $validator!: Validator;
  public loading = false;
  public integration: ConfigValue | null = null;
  public traceabilityConfig!: TraceabilityConfig;
  public location: Location | null = null;
  public hasConfig = false;
  public integrationDisable: boolean = false;
  public routeHandler: boolean = true;
  public uriReg = /^(https?:\/\/)([\da-z\.-]+\.[a-z\.]{2,6}|[\d\.]+)*$/;
  public integrationModel: IntegrationModel = {
    enabled: false,
    location_key: null,
    vendor_key: null,
    license: null,
    base_uri: null,
    port: null,
    user_credentials_required: true
  };
  public biotrackIntegrationModel: BiotrackIntegrationModel = {
    enabled: false,
    license: null,
    base_uri: null,
    ubi: null,
    user_credentials_required: false,
    starting_inventory_date: null,
    location_key: null
  };
  public renderModal = false;

  public get enabledLabel(): string {
    return this.integrationModel.enabled
      ? String(i18n.t("stems.enable"))
      : String(i18n.t("stems.disable"));
  }

  public get enabledBiotrackLabel(): string {
    return this.biotrackIntegrationModel.enabled
      ? String(i18n.t("stems.enable"))
      : String(i18n.t("stems.disable"));
  }

  public renderAnimationModal(ModalInfo: string) {
    this.$modals.load(
      IntegrationLoadingModal,
      {
        size: "normal",
        positionX: "center",
        positionY: "center"
      },
      {
        connectionModalData: {
          title: "traceability.state_traceability_integration.title",
          info: ModalInfo
        }
      }
    );
  }

  public renderIntegrationStatusModal(modalsInfo: ModalIntegrationPopUp) {
    this.$modals
      .load(
        IntegrationPopUpModalComponent,
        { size: "normal", positionX: "center", positionY: "center" },
        {
          connectionModalData: modalsInfo
        }
      )
      .catch(() => {
        // Nothing to do on close
      })
      .finally(() => {
        if (this.routeHandler) {
          this.cancel();
        }
      });
  }

  public renderIntegrationEnabledStatusModal(
    modalsInfo: ModalIntegrationPopUp
  ) {
    this.$modals
      .load(
        IntegrationPopUpModalComponent,
        { size: "normal", positionX: "center", positionY: "center" },
        {
          connectionModalData: modalsInfo
        }
      )
      .catch(() => {
        // Nothing to do on close
      })
      .finally(async () => {
        this.renderAnimationModal(
          "traceability.state_traceability_integration.sync"
        );
        await traceabilityService.setBiotrackIntegrationConfigSync();
        this.cancel();
      });
  }

  public cancel() {
    this.$router.back();
  }

  public async save() {
    if (await this.$validator.validateAll()) {
      if (this.traceabilityConfig.value === "Metrc") {
        if (!this.integrationModel.enabled) {
          const confirm = (await this.$modals
            .load(
              ConfirmModalComponent,
              { size: "normal", positionX: "center", positionY: "center" },
              {
                modalData: {
                  title: "traceability.integration_form.disable_title",
                  body: "traceability.integration_form.disable_msg",
                  titleClass: "error_title"
                }
              }
            )
            .catch(() => false)) as boolean;
          this.$router.push({
            name: "location-api-management"
          });
          if (!confirm) {
            return;
          }
        }
        const result = await traceabilityService.setIntegrationConfig(
          this.location!,
          this.integrationModel,
          this.hasConfig
        );
        const locationId =
          (result && result.location_id) || +this.$route.params.id;
        traceabilityService.setStateTraceabilitySystem(
          this.integrationModel.enabled,
          locationId
        );
        this.processMetrcRes(result);
      } else {
        if (this.biotrackIntegrationModel.enabled) {
          this.renderAnimationModal(
            "traceability.state_traceability_integration.initiate"
          );
          this.renderAnimationModal(
            "traceability.state_traceability_integration.check"
          );
        }
        const result = await traceabilityService.setBiotrackIntegrationConfig(
          this.location!,
          this.biotrackIntegrationModel,
          this.hasConfig
        );
        if (
          typeof result === "string" &&
          !this.biotrackIntegrationModel.enabled
        ) {
          await this.axios.request({
            url: "/services/bdi/configs_values/biotrack_traceability_config",
            method: this.hasConfig ? "PUT" : "POST",
            data: {
              location_id: this.location!.id,
              value: JSON.stringify({
                starting_inventory_date: `${
                  this.biotrackIntegrationModel.starting_inventory_date
                }`,
                base_uri: `${this.biotrackIntegrationModel.base_uri}`,
                ubi: `${this.biotrackIntegrationModel.ubi}`,
                enabled: this.biotrackIntegrationModel.enabled
              })
            }
          });
        }
        if (
          typeof result === "object" ||
          (typeof result === "string" && !this.biotrackIntegrationModel.enabled)
        ) {
          traceabilityService.setBiotrackTraceabilitySystem(
            this.biotrackIntegrationModel.enabled,
            String(this.biotrackIntegrationModel.starting_inventory_date!),
            +this.$route.params.id!
          );
        }
        this.processRes(result);
        if (
          this.biotrackIntegrationModel.enabled &&
          typeof result === "string" &&
          result.includes(
            "The credentials for the current BioTrack Traceability user are missing"
          )
        ) {
          EventBus.$on("acceptHandler", () => {
            this.routeHandler = false;
          });
        }
      }
    }
  }

  public async startTraceabilityIntegration(locationId: string) {
    if (this.traceabilityConfig.value === "Metrc") {
      const integrations = await traceabilityService.getTraceabilityConfig([
        +locationId
      ]);
      if (integrations.length) {
        this.hasConfig = true;
        this.integration = integrations[0];
        const saved = JSON.parse(this.integration.value);
        const stripUrl = saved.base_uri.split(":");
        if (stripUrl.length === 3) {
          saved.port = stripUrl.pop();
          saved.base_uri = stripUrl.join(":");
        }
        this.integrationModel = { ...this.integrationModel, ...saved };
      }
    }
    // set traceability for bio-track
    else {
      const integrations = await traceabilityService.getBiotrackTraceabilityConfig(
        [+locationId]
      );
      if (integrations.length) {
        this.hasConfig = true;
        this.integration = integrations[0];
        const saved = JSON.parse(this.integration.value);
        this.biotrackIntegrationModel = {
          ...this.biotrackIntegrationModel,
          ...saved
        };
      }
    }
    this.loading = false;
  }

  public async mounted() {
    if (this.$route.name === "trace-integration-view") {
      this.integrationDisable = true;
    }
    this.loading = true;
    const locationId = this.$route.params.id;
    this.location = await locationsService.findById(+locationId, {
      embed: "state"
    });
    await traceabilityService
      .getCurrentTraceability({ location_id: locationId })
      .then(value => {
        if (value.length && value[0]) {
          this.traceabilityConfig = value[0];
        }
      });
    this.startTraceabilityIntegration(locationId);
  }

  protected processMetrcRes(res: ConfigValue[] | null) {
    const msgModal: ModalNotification = {
      title: "traceability.integration_form.metrc_enabled",
      body: "traceability.integration_form.mtrc_msg_ok",
      captionButton: "OK",
      titleClass: "success_title"
    };

    if (!res) {
      msgModal.title = "smth_wrong";
      msgModal.body = "traceability.integration_form.metrc_msg_fail";
      msgModal.titleClass = "error_title";
    }
    if ((res && this.integrationModel.enabled) || !res) {
      this.$modals
        .load(
          AlertComponent,
          { size: "normal", positionX: "center", positionY: "center" },
          { msgModal }
        )
        .catch(() => {
          // Nothing to do on close
        })
        .finally(() => {
          if (res) {
            this.cancel();
          }
        });
    }
  }

  protected processRes(res: ConfigValue[] | boolean | string) {
    if (res) {
      this.renderModal = true;
    }
    if (this.biotrackIntegrationModel.enabled && res) {
      this.renderAnimationModal(
        "traceability.state_traceability_integration.initiate"
      );
      this.renderAnimationModal(
        "traceability.state_traceability_integration.check"
      );
    }

    if (typeof res === "object" && this.biotrackIntegrationModel.enabled) {
      this.renderIntegrationEnabledStatusModal({
        title: "traceability.state_traceability_integration.title",
        status: "traceability.state_traceability_integration.enabled.info",
        icon: { name: "fas fa-check-circle", color: "success" },
        accept: "traceability.state_traceability_integration.accept",
        message: "traceability.state_traceability_integration.enabled.message",
        description:
          "traceability.state_traceability_integration.enabled.description",
        cancelButton: false,
        acceptButton: true
      });
    } else if (
      this.biotrackIntegrationModel.enabled &&
      typeof res === "string" &&
      res.includes(
        "The credentials for the current BioTrack Traceability user are missing"
      )
    ) {
      this.renderIntegrationStatusModal({
        title: "traceability.state_traceability_integration.title",
        status:
          "traceability.state_traceability_integration.credential_requirements.info",
        icon: { name: "fas fa-times-circle", color: "red" },
        accept:
          "traceability.state_traceability_integration.credential_requirements.yes",
        cancel:
          "traceability.state_traceability_integration.credential_requirements.no",
        message:
          "traceability.state_traceability_integration.credential_requirements.message",
        description:
          "traceability.state_traceability_integration.credential_requirements.description",
        cancelButton: true,
        acceptButton: true
      });
    } else if (!this.biotrackIntegrationModel.enabled) {
      this.renderIntegrationStatusModal({
        title: "traceability.state_traceability_integration.title",
        status: "traceability.state_traceability_integration.disabled.info",
        icon: { name: "fas fa-exclamation-triangle", color: "red" },
        accept: "traceability.state_traceability_integration.accept",
        message: "traceability.state_traceability_integration.disabled.message",
        description:
          "traceability.state_traceability_integration.disabled.description",
        cancelButton: false,
        acceptButton: true
      });
    } else if (
      this.biotrackIntegrationModel.enabled &&
      typeof res === "string" &&
      res.includes("Wrong BioTrack Traceability credentials.")
    ) {
      this.renderIntegrationStatusModal({
        title: "traceability.state_traceability_integration.title",
        status: "traceability.state_traceability_integration.failed.info",
        icon: { name: "fas fa-exclamation-triangle", color: "red" },
        accept: "traceability.state_traceability_integration.accept",
        cancel: "traceability.state_traceability_integration.failed.no",
        message: "traceability.state_traceability_integration.failed.message",
        description:
          i18n.t(
            "traceability.state_traceability_integration.failed.description"
          ) + `${res}`,
        cancelButton: false,
        acceptButton: true
      });
    }
  }
}
